import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

import { cn } from '@/lib/utils';

// Egységes Select — natív csoport-támogatással (SelectGroup + SelectLabel). A többszörös kiválasztás (multi-select
// combobox) külön MultiSelect komponens lesz, amikor egy modul igényli (nem duplikálunk grouped-selectet).
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'border-border input-glass text-md flex h-8 w-full items-center justify-between whitespace-nowrap rounded-lg border px-2.5 py-1',
      // Radix a placeholder-állapotban `data-placeholder`-t tesz a triggerre (NEM natív ::placeholder) → a
      // helyes szelektor `data-[placeholder]:`; tertiary (halvány hint), egyezően az input placeholderével.
      'data-[placeholder]:text-subtle focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
      // A FELIRAT CSONKOLÓDIK, ha nem fér ki (mért hiba: a hosszú opció — „Kategorizálatlan", „Bizonylat
      // szerinti " — KILÓGOTT a triggerből, ellipszis nélkül). A `min-w-0` a kulcs: flex-
      // gyerekként a span alap `min-width:auto`-val NEM zsugorodik a tartalma alá, tehát nincs mit csonkolni.
      // A `truncate` (és nem `line-clamp-1`) azért, mert a trigger `whitespace-nowrap` — egy soros szövegen a
      // `text-overflow: ellipsis` a kiszámítható út.
      '[&>span]:min-w-0 [&>span]:truncate',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        'dark-surface-popover text-popover-foreground relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        position === 'popper' && 'data-[side=bottom]:translate-y-1',
        // A PANEL NEM LÓGHAT LE A KÉPERNYŐRŐL. A `max-h-96` (384 px) FIX korlát, ami nem tud a viewportról:
        // egy alacsony ablakban (vagy egy alul álló soron) a sok opciós legördülő túlgördült a képernyőn, és
        // az alsó opciók elérhetetlenek voltak. A Radix popper KISZÁMOLJA a ténylegesen rendelkezésre álló
        // magasságot (ütközés-figyeléssel) — a `min()` a kettő közül a szűkebbet veszi, tehát a panel sem
        // nem lóg le, sem nem nő aránytalanul nagyra. (Csak `popper` pozícióban van meg a változó; az
        // `item-aligned` módban a Radix maga méretez.)
        position === 'popper' && 'max-h-[min(24rem,var(--radix-select-content-available-height))]',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
        <ChevronUpIcon className="h-4 w-4" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
        <ChevronDownIcon className="h-4 w-4" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    // OPTGROUP-CÍMKE: halványabb (`text-subtle`) + NAGYBETŰS + ritkított — így vizuálisan ELVÁLIK az
    // opcióktól (user-döntés: a korábbi `muted` + kisbetűs alak összeolvadt a sorokkal).
    className={cn(
      'text-subtle px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    /**
     * FA-BEHÚZÁS: az opció MÉLYSÉGE a fában (0 = gyökér-szint). Minden szint egy behúzás + egy hajszálvékony
     * vezetővonal — a korábbi `· ` szöveg-előtag helyett, ami nem behúzás volt, hanem írásjel a névben (és a
     * kiválasztott értékbe is beleragadt).
     *
     * MIÉRT SZÁM, és miért nem `indented` bool: a kiadás-kategóriák fája HÁROM szintű lett (főkategória →
     * alkategória → tétel), és egyetlen bool a második és a harmadik szintet UGYANOTT húzta volna be — a
     * tétel a saját alkategóriája mellé, nem alá.
     *
     * MIÉRT AZ `ItemText`-EN KÍVÜL: a trigger a KIVÁLASZTOTT opció `ItemText`-jét tükrözi. A behúzás az
     * OPCIÓ-LISTA rendje, nem az érték része — odabent a jel a triggerbe is átmenne, ahol nincs mihez
     * képest behúzni.
     */
    depth?: number;
  }
>(({ className, children, depth = 0, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'text-md relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-8 outline-none',
      'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    {Array.from({ length: Math.max(0, depth) }, (_, i) => (
      <span key={i} aria-hidden className="bg-border ml-1 mr-2.5 h-4 w-px shrink-0" />
    ))}
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem };
